import { CRM_FIELD_TYPE, CrmFilterField } from "types/Models/crmFields";

/** Rows in the illustrative preview table - a fixed, small sample so the preview stays cheap
 * to render no matter how many columns are picked. */
export const PREVIEW_ROW_COUNT = 8;

const PLACEHOLDER_PEOPLE = [
  "Priya Nair",
  "Dev Shah",
  "Mara Kovacs",
  "Theo Marsh",
  "Rosa Lin",
  "Alex Osei",
];

const PLACEHOLDER_WORDS = [
  "Cascade Labs",
  "Northwind Robotics",
  "Hale & Vance",
  "Orbit Freight",
  "Driftwood Studio",
  "Lumen Analytics",
  "Paragon Biotech",
  "Fernwood Capital",
];

const hashString = (value: string): number => {
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash * 31 + value.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
};

const isPersonField = (label: string) =>
  /name|manager|associate|owner/i.test(label);

/**
 * A deterministic placeholder value for one (field, row) pair - same inputs always produce the
 * same output, so the preview never flickers between renders, and real CRM data is sparse
 * (most entities don't have every field filled in), so roughly one cell in five is left blank
 * here too rather than showing a fully-populated grid that doesn't match reality.
 */
export const generatePreviewCell = (
  field: CrmFilterField,
  rowIndex: number
): string => {
  const seed = hashString(field.field_key) + rowIndex;
  if (seed % 5 === 0) {
    return "";
  }

  if (field.filter_kind === "CHECKBOX") {
    return seed % 2 === 0 ? "Yes" : "No";
  }
  if (field.filter_kind === "SINGLE_SELECT" && field.options.length > 0) {
    return field.options[seed % field.options.length].label;
  }
  if (field.filter_kind === "MULTI_SELECT" && field.options.length > 0) {
    const count = (seed % 2) + 1;
    return field.options
      .slice(0, count)
      .map((option) => option.label)
      .join("; ");
  }
  if (field.filter_kind === "NUMBER") {
    const amount = 100 + (seed % 900);
    return field.field_type === CRM_FIELD_TYPE.CURRENCY
      ? `$${amount}.00`
      : String(amount);
  }
  if (field.filter_kind === "DATE") {
    const date = new Date(2026, seed % 12, (seed % 27) + 1);
    return date.toISOString().slice(0, 10);
  }
  return isPersonField(field.label)
    ? PLACEHOLDER_PEOPLE[seed % PLACEHOLDER_PEOPLE.length]
    : PLACEHOLDER_WORDS[seed % PLACEHOLDER_WORDS.length];
};
